import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Mail, CheckCircle2, ArrowRight } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { notifyAdminSignupVerified } from "@/lib/admin-notify.functions";

/**
 * Supabase auth callback. Landing page for every confirmation / recovery /
 * magic-link email. Exchanges the PKCE ?code=... (or reads OTP hash tokens)
 * into a session long enough to confirm that `email_confirmed_at` is
 * populated, then explicitly signs the user OUT and shows a success screen
 * asking them to sign in.
 *
 * Why the forced sign-out: the confirmation link may be opened on a
 * different device/browser than the one they signed up on, and we never
 * want to send a user straight through to a paywall (or, worse, the
 * member dashboard) from a magic-link session that surprises them. The
 * "Sign in and complete membership" button carries `next=/membership`
 * through login so the post-login screen is Step 2 of 4, where they choose
 * Stripe or PayPal themselves.
 */
export const Route = createFileRoute("/auth/callback")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>) =>
    z
      .object({
        code: z.coerce.string().optional(),
        email: z.coerce.string().optional(),
        token: z.coerce.string().optional(),
        token_hash: z.coerce.string().optional(),
        type: z.coerce.string().optional(),
        next: z.coerce.string().optional(),
        error: z.coerce.string().optional(),
        error_description: z.coerce.string().optional(),
      })
      .parse(search),
  head: () => ({
    meta: [
      { title: "Email confirmed — Recovery Trauma™" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthCallbackPage,
});

const DEFAULT_NEXT = "/membership";

function sanitizeNext(next: string | undefined): string {
  if (!next) return DEFAULT_NEXT;
  if (!next.startsWith("/") || next.startsWith("//")) return DEFAULT_NEXT;
  return next;
}

function AuthCallbackPage() {
  const search = Route.useSearch();
  const [err, setErr] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const next = sanitizeNext(search.next);
  const isGift = next.startsWith("/redeem-gift");
  const loginHref = isGift ? `${next}${next.includes("?") ? "&" : "?"}login=1` : `/login?next=${encodeURIComponent(next)}`;

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (search.error) {
        setErr(search.error_description || search.error);
        return;
      }

      try {
        let verifiedEmail = "";
        let displayName: string | null = null;

        const markConfirmed = async () => {
          if (verifiedEmail) {
            notifyAdminSignupVerified({
              data: { email: verifiedEmail, name: displayName, source: "email_confirmation" },
            }).catch(() => { /* noop */ });
          }
          try { await supabase.auth.signOut(); } catch { /* noop */ }
          try { sessionStorage.removeItem("rt_pending_verify_email"); } catch { /* noop */ }
          if (!cancelled) setConfirmed(true);
        };

        // Preferred cross-device flow from our branded signup emails: email
        // + one-time token. This avoids PKCE/code_verifier localStorage, so the
        // confirmation link works on a different phone/browser/laptop.
        if (search.email && search.token && search.type) {
          const { data, error } = await supabase.auth.verifyOtp({
            email: search.email,
            token: search.token,
            type: search.type as any,
          });
          if (error) {
            setErr(error.message);
            return;
          }
          if (data.user || data.session?.user) {
            const user = data.user ?? data.session?.user;
            verifiedEmail = user?.email ?? search.email;
            displayName =
              (user?.user_metadata?.display_name as string | undefined) ??
              (user?.user_metadata?.full_name as string | undefined) ??
              null;
            await markConfirmed();
            return;
          }
        } else if (search.token_hash && search.type) {
          // Secondary cross-device flow for providers that include token_hash.
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: search.token_hash,
            type: search.type as any,
          });
          if (error) {
            setErr(error.message);
            return;
          }
          // On some confirmation links the email is verified successfully but
          // no browser session is created on this device. The returned user is
          // still authoritative, so do not fall through to the timeout screen.
          if (data.user || data.session?.user) {
            const user = data.user ?? data.session?.user;
            verifiedEmail = user?.email ?? "";
            displayName =
              (user?.user_metadata?.display_name as string | undefined) ??
              (user?.user_metadata?.full_name as string | undefined) ??
              null;
            await markConfirmed();
            return;
          }
        } else if (search.code) {
          // Legacy PKCE flow (same-device only).
          const { error } = await supabase.auth.exchangeCodeForSession(search.code);
          if (error) {
            setErr(error.message);
            return;
          }
        }

        // Wait briefly for the session to hydrate and email_confirmed_at
        // to populate. Covers PKCE + hash-flow parsing races.
        const deadline = Date.now() + 8_000;
        while (!cancelled) {
          const { data } = await supabase.auth.getUser();
          if (data.user?.email_confirmed_at) {
            verifiedEmail = data.user.email ?? "";
            displayName =
              (data.user.user_metadata?.display_name as string | undefined) ??
              (data.user.user_metadata?.full_name as string | undefined) ??
              null;
            await markConfirmed();
            return;
          }
          if (Date.now() > deadline) {
            setErr(
              "We couldn't confirm your email on this device. Please open the link again from your inbox, or sign in below.",
            );
            return;
          }
          await new Promise((r) => setTimeout(r, 400));
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Something went wrong confirming your email.");
      }
    }

    run();
    return () => {
      cancelled = true;
    };
    }, [search.code, search.email, search.token, search.token_hash, search.type, search.error, search.error_description]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg items-center justify-center px-4">
      <div className="w-full rounded-3xl border border-gold/30 bg-card p-8 text-center shadow-[var(--shadow-gold)]">
        {err ? (
          <>
            <AlertTriangle className="mx-auto h-10 w-10 text-destructive" aria-hidden />
            <h1 className="mt-4 font-display text-2xl text-foreground">We couldn't confirm your link</h1>
            <p className="mt-2 text-sm text-muted-foreground">{err}</p>
            <div className="mt-6 flex flex-col items-center gap-3">
              <a
                href={loginHref}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-gold)] px-5 py-3 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)]"
              >
                Sign in to continue
              </a>
              <a
                href="/verify-email"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground underline"
              >
                <Mail className="h-4 w-4" aria-hidden /> Back to email verification
              </a>
            </div>
          </>
        ) : confirmed ? (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-[var(--gold-deep)]" aria-hidden />
            <h1 className="mt-4 font-display text-2xl text-foreground">
              Your email address has been confirmed
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground/80">
              {isGift
                ? "Please log in with the email and password you created — your gift code is saved and your gifted membership will activate straight away. No payment needed."
                : "Please sign in using the email address and password you created. After signing in, you'll be taken to your Membership page where you can choose how to complete your Membership — with Stripe or PayPal."}
            </p>
            <div className="mt-6 flex flex-col items-center gap-3">
              <a
                href={loginHref}
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-5 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5"
              >
                Sign in to continue <ArrowRight className="h-4 w-4" />
              </a>
              <p className="text-xs text-muted-foreground">
                {isGift ? "Your gift activates as soon as you log in." : "Your Membership is not active yet — you'll choose Stripe or PayPal on the next screen."}
              </p>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[var(--gold-deep)]" aria-hidden />
            <h1 className="mt-4 font-display text-2xl text-foreground">Confirming your email…</h1>
            <p className="mt-2 text-sm text-muted-foreground">One moment.</p>
          </>
        )}
      </div>
    </div>
  );
}
